import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { useAuth } from "../../shared/auth/AuthProvider";
import { studentReviewApi as api, studentReviewError } from "../../shared/reviews/studentReviewApi";
import { fullReviewTextStyle, ReviewError, ReviewReference, StudentReviewAuthor, StudentReviewStatus, studentReviewDate } from "../../shared/reviews/StudentReviewPresentation";
import EditMyReviewModal from "../../shared/reviews/EditMyReviewModal";

export default function MyCourseReviews() {
  const { user, loading } = useAuth();
  if (loading) return <div role="status">กำลังตรวจสอบบัญชี...</div>;
  if (!user?.uid) return <Alert variant="warning">กรุณาเข้าสู่ระบบเพื่อดูรีวิวของฉัน</Alert>;
  return <OwnReviews key={user.uid} />;
}
function OwnReviews() {
  const [reviews, setReviews] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [next, setNext] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);
  const reloadButton = useRef(null);
  const missing = useRef(false);
  useEffect(() => {
    let active = true; setLoading(true); setError(""); setReviews([]); setNext(null);
    api.list({ pageSize: 10, ...(cursor ? { cursor } : {}) }).then((result) => {
      if (active) { setReviews(result.reviews); setNext(result.nextCursor); }
    }).catch((failure) => { if (active) setError(studentReviewError(failure)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cursor, attempt]);
  useEffect(() => { if (!editing && missing.current) { reloadButton.current?.focus(); missing.current = false; } }, [editing]);
  const close = () => { setEditing(null); };
  return <div>
    <h4>รีวิวของฉัน</h4><p className="text-muted">ดูและแก้ไขรีวิวที่คุณส่งไว้ การแสดงบนหน้าแรกขึ้นอยู่กับการตั้งค่าของคอร์สและสถานะรีวิว</p>
    {loading && <div role="status">กำลังโหลดรีวิว...</div>}
    <ReviewError message={error} retry={() => setAttempt((value) => value + 1)} busy={loading} />
    {notice && <Alert variant="success" role="status">{notice}</Alert>}
    {!loading && !error && !reviews.length && <p>คุณยังไม่มีรีวิว</p>}
    {reviews.map((review) => <Card className="mb-3" key={review.reviewId}><Card.Body>
      <div className="d-flex justify-content-between align-items-start gap-2"><StudentReviewAuthor review={review} /><StudentReviewStatus status={review.status} /></div>
      <div className="small text-muted mt-2">วันที่รีวิว {studentReviewDate(review.createdAt)}</div>
      {review.updatedAt !== review.createdAt && <div className="small text-muted">แก้ไขล่าสุด {studentReviewDate(review.updatedAt)}</div>}
      <p className="mt-3" style={fullReviewTextStyle}>{review.reviewText}</p><p className="text-muted small" style={{ overflowWrap: "anywhere" }}>{review.courseName}</p>
      <Button variant="outline-primary" disabled={loading} onClick={() => { setEditing(review); setNotice(""); }}>แก้ไขรีวิว</Button>
      <ReviewReference reviewId={review.reviewId} />
    </Card.Body></Card>)}
    <div className="d-flex gap-2 flex-wrap">
      <Button ref={reloadButton} variant="outline-secondary" disabled={loading || !!editing} onClick={() => { setCursor(null); setAttempt((value) => value + 1); }}>โหลดรายการใหม่</Button>
      {cursor && <Button disabled={loading || !!editing} variant="outline-secondary" onClick={() => setCursor(null)}>หน้าแรก</Button>}
      {next && <Button disabled={loading || !!editing} onClick={() => setCursor(next)}>หน้าถัดไป</Button>}
    </div>
    {editing && <EditMyReviewModal key={editing.reviewId} review={editing} onHide={close}
      onMissing={(id) => { missing.current = true; setReviews((previous) => previous.filter((review) => review.reviewId !== id)); }}
      onSaved={(canonical) => { setReviews((previous) => previous.map((review) => review.reviewId === canonical.reviewId ? canonical : review)); setNotice("บันทึกรีวิวเรียบร้อยแล้ว"); close(); }} />}
  </div>;
}
